import { createSignal, onCleanup, type Accessor } from "solid-js";
import type { ApiError, Result } from "../api/result.ts";

/**
 * The three states of anything fetched. Screens render this with `<Switch>`
 * instead of juggling loading and error flags.
 */
export type Loaded<T> =
  | { state: "loading" }
  | { state: "loaded"; value: T }
  | { state: "failed"; error: ApiError };

export type Loader<T> = {
  value: Accessor<Loaded<T>>;
  /**
   * Runs the fetch again, back through the loading state. Awaitable, so a
   * caller that has to touch the DOM the new value renders can wait for it.
   */
  reload: () => Promise<void>;
  /** Replaces the loaded value in place, for optimistic edits. */
  put: (value: T) => void;
};

/**
 * Wraps one fetch into a Loaded signal. The fetch is a thunk, so callers can
 * close over reactive parameters and reload with the current ones.
 */
export const createLoader = <T>(fetcher: () => Promise<Result<T>>): Loader<T> => {
  const [value, setValue] = createSignal<Loaded<T>>({ state: "loading" });

  const run = async () => {
    setValue({ state: "loading" });
    const result = await fetcher();
    setValue(result.ok ? { state: "loaded", value: result.value } : { state: "failed", error: result.error });
  };

  void run();

  return {
    value,
    reload: run,
    put: (next) => setValue({ state: "loaded", value: next }),
  };
};

/** The loaded value, or undefined while loading or after a failure. */
export const loadedValue = <T>(loaded: Loaded<T>): T | undefined =>
  loaded.state === "loaded" ? loaded.value : undefined;

export type Debouncer<T> = {
  /** Replaces whatever was pending for this key and restarts its timer. */
  push: (key: string, value: T) => void;
  /** Runs every pending call right away and resolves once they are all done. */
  flush: () => Promise<void>;
};

/**
 * Trailing debounce with one timer per key, so typing in one field never
 * delays the call for another. `flush` covers the moments where waiting is
 * wrong, such as a submit that must not leave the last keystroke behind.
 */
export const createDebouncer = <T>(
  delay: number,
  run: (value: T) => Promise<void>,
): Debouncer<T> => {
  type Pending = { value: T; timer: ReturnType<typeof setTimeout> };
  const pending = new Map<string, Pending>();

  const fire = (key: string): Promise<void> => {
    const entry = pending.get(key);
    if (entry === undefined) return Promise.resolve();
    pending.delete(key);
    clearTimeout(entry.timer);
    return run(entry.value);
  };

  onCleanup(() => {
    for (const entry of pending.values()) clearTimeout(entry.timer);
    pending.clear();
  });

  return {
    push: (key, value) => {
      const existing = pending.get(key);
      if (existing !== undefined) clearTimeout(existing.timer);
      pending.set(key, { value, timer: setTimeout(() => void fire(key), delay) });
    },
    flush: async () => {
      await Promise.all([...pending.keys()].map(fire));
    },
  };
};
